import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import FilterPanel from '../../components/FilterPanel';
import DataTable from '../../components/DataTable';
import { formatDate, placeLabel } from '../../utils/formatters';
import {
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
const EVENT_LABELS = {
  AUTO_VISIT_FEATURE_ENABLED: 'הפיצ׳ר הופעל',
  AUTO_VISIT_FEATURE_DISABLED: 'הפיצ׳ר כובה',
  AUTO_GEOFENCES_REFRESH_STARTED: 'רענון Geofence התחיל',
  AUTO_GEOFENCES_REFRESH_SUCCEEDED: 'רענון הסתיים',
  AUTO_GEOFENCES_REGISTERED: 'מקומות נרשמו',
  AUTO_GEOFENCES_REFRESH_FAILED: 'רישום Geofence נכשל',
  AUTO_GEOFENCE_EVENT_ERROR: 'שגיאת Geofence',
  AUTO_PLACE_DWELL_DETECTED: 'DWELL התקבל',
  AUTO_VERIFY_STARTED: 'אימות GPS התחיל',
  AUTO_VERIFY_SUCCESS: 'אימות GPS הצליח',
  AUTO_VERIFY_FAILED: 'אימות GPS נכשל',
  AUTO_SESSION_STARTED: 'Session אוטומטי התחיל',
  AUTO_SESSION_START_FAILED: 'פתיחת Session נכשלה',
  AUTO_SESSION_SKIPPED_ACTIVE_SESSION: 'דילוג: Session פעיל',
  AUTO_SESSION_SKIPPED_COOLDOWN: 'דילוג: Cooldown',
  AUTO_SESSION_SKIPPED: 'Session נדחה',
  AUTO_STATUS_SNAPSHOT: 'צילום מצב',
  AUTO_CONFIG_SYNC_STARTED: 'סנכרון הגדרות התחיל',
  AUTO_CONFIG_ALREADY_CURRENT: 'ההגדרות כבר מעודכנות',
  AUTO_CONFIG_UPDATED: 'הגדרות עודכנו ב-Native',
  AUTO_CONFIG_SYNC_FAILED: 'סנכרון הגדרות נכשל',
};
const SEVERITY_LABELS = {
  info: 'מידע',
  warning: 'אזהרה',
  error: 'שגיאה',
};
const detailText = (details = {}) =>
  Object.entries(details)
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : String(value)}`)
    .join(' · ');
const pillSx = (severity) => ({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '26px',
  borderRadius: '999px',
  padding: '0 11px',
  fontWeight: '800',
  fontSize: '12px',
  ...(severity === 'error'
    ? {
        background: '#fee4e2',
        color: '#b42318',
      }
    : severity === 'warning'
      ? {
          background: '#fff6d6',
          color: '#b54708',
        }
      : {
          background: '#eef2ff',
          color: '#3730a3',
        }),
});
const SummaryCard = ({ label, value, tone = 'neutral' }) => {
  const colors = {
    neutral: '#98a2b3',
    danger: '#d92d20',
    info: '#1677ff',
  };
  return (
    <Card
      sx={{
        minHeight: 86,
        display: 'grid',
        alignContent: 'center',
        gap: '4px',
        p: '14px 18px',
        border: '1px solid rgba(20,45,41,.11)',
        borderRight: `4px solid ${colors[tone]}`,
        borderRadius: '16px',
        boxShadow: 'var(--shadow-sm)',
        background: 'var(--panel)',
      }}
    >
      <Typography
        color="text.secondary"
        sx={{
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 28,
          fontWeight: 800,
        }}
      >
        {value || 0}
      </Typography>
    </Card>
  );
};
const DetailItem = ({ label, children, ltr = false }) => (
  <Stack
    sx={{
      p: 1,
      bgcolor: 'background.default',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      minWidth: 0,
      gap: '4px',
    }}
  >
    <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 700 }}>{label}</Typography>

    <Typography dir={ltr ? 'ltr' : 'rtl'} sx={{ fontSize: 14, overflowWrap: 'anywhere' }}>
      {children ?? '-'}
    </Typography>
  </Stack>
);

const EventDialog = ({ event, onClose }) => {
  if (!event) return null;
  const details = event.details || {};
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px', background: 'var(--panel)', boxShadow: 'var(--shadow-lg)' },
      }}
    >
      <DialogTitle sx={{ p: '20px 24px 16px' }}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
        >
          <Stack sx={{ gap: '6px' }}>
            <Typography sx={{ color: 'var(--primary)', fontSize: 12, fontWeight: 800 }}>
              דיווח אוטומטי
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
              {EVENT_LABELS[event.eventType] || event.eventType}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
              {formatDate(event.occurredAt)}
            </Typography>
          </Stack>
          <Typography sx={pillSx(event.severity)}>
            {SEVERITY_LABELS[event.severity] || event.severity}
          </Typography>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: '22px 24px', display: 'grid', gap: '22px' }}>
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 2 }}>
          <DetailItem label="סוג אירוע">{event.eventType}</DetailItem>
          <DetailItem label="סיבה">{event.reason || '-'}</DetailItem>
          <DetailItem label="מקום">{placeLabel(event.place)}</DetailItem>
          <DetailItem label="Place ID">{event.placeId || '-'}</DetailItem>
          <DetailItem label="Device ID">{event.deviceId || '-'}</DetailItem>
          <DetailItem label="Anonymous ID">{event.anonymousId || '-'}</DetailItem>
          <DetailItem label="גרסת אפליקציה">{event.appVersion || '-'}</DetailItem>
          <DetailItem label="גרסת Config">{event.configVersion ?? '-'}</DetailItem>
          <DetailItem label="גרסת Geofence">{event.geofenceVersion || '-'}</DetailItem>
          <DetailItem label="Event ID">{event.eventId || event._id}</DetailItem>
          <DetailItem label="זמן האירוע">{formatDate(event.occurredAt)}</DetailItem>
          <DetailItem label="זמן קבלה בשרת">{formatDate(event.createdAt)}</DetailItem>
        </Stack>
        <Divider />
        <Stack sx={{ gap: '14px' }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800 }}>פרטים מלאים</Typography>
          {Object.keys(details).length ? (
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
                gap: '14px',
                '@media (max-width: 700px)': { gridTemplateColumns: '1fr' },
              }}
            >
              {Object.entries(details).map(([key, value]) => (
                <Stack
                  key={key}
                  sx={{
                    p: '13px 14px',
                    border: '1px solid var(--line)',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    minWidth: 0,
                    gap: '6px',
                  }}
                >
                  <Typography sx={{ color: 'text.secondary', fontSize: 12, fontWeight: 800 }}>
                    {key}
                  </Typography>

                  {Array.isArray(value) ? (
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: '6px' }}>
                      {value.map((item, index) => (
                        <Chip
                          key={`${item}-${index}`}
                          size="small"
                          label={String(item)}
                          sx={{
                            maxWidth: '100%',
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: 14, overflowWrap: 'anywhere' }}>
                      {String(value)}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">אין פרטים נוספים באירוע הזה.</Typography>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: '16px 24px', justifyContent: 'flex-start' }}>
        <Button
          onClick={onClose}
          sx={{
            background: '#edf2f7',
            color: 'var(--text)',
            '&:hover': { background: '#ddebff', color: '#1269e8' },
          }}
        >
          סגירה
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default function AutoVisitReportsPage({ request }) {
  const [data, setData] = useState({
    items: [],
    total: 0,
    page: 1,
    limit: 50,
    summary: {},
  });
  const [filters, setFilters] = useState({
    eventType: '',
    severity: '',
    placeId: '',
    deviceId: '',
    from: '',
    to: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const load = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: '50',
        });
        Object.entries(filters).forEach(([key, value]) => value && params.set(key, value));
        setData(await request(`/bo/auto-visit-events?${params}`));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, request],
  );
  useEffect(() => {
    load(1);
  }, [load]);
  const columns = useMemo(
    () => [
      {
        key: 'occurredAt',
        label: 'זמן',
        minWidth: 145,
        render: (event) => formatDate(event.occurredAt),
      },
      {
        key: 'eventType',
        label: 'אירוע',
        minWidth: 190,
        render: (event) => (
          <Typography sx={pillSx(event.severity)}>
            {EVENT_LABELS[event.eventType] || event.eventType}
          </Typography>
        ),
      },
      {
        key: 'place',
        label: 'מקום',
        minWidth: 165,
        render: (event) => (
          <Stack
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              {placeLabel(event.place)}
            </Typography>
            {event.placeId && (
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: 11,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {event.placeId}
              </Typography>
            )}
          </Stack>
        ),
      },
      {
        key: 'device',
        label: 'מכשיר',
        minWidth: 170,
        render: (event) => event.deviceId || event.anonymousId || '-',
      },
      {
        key: 'reason',
        label: 'סיבה',
        minWidth: 150,
        render: (event) => event.reason || '-',
      },
      {
        key: 'details',
        label: 'פרטים',
        minWidth: 260,
        render: (event) => (
          <Typography
            title={detailText(event.details)}
            sx={{
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {detailText(event.details) || '-'}
          </Typography>
        ),
      },
      {
        key: 'versions',
        label: 'גרסאות',
        minWidth: 120,
        render: (event) => `App ${event.appVersion || '-'} · Config ${event.configVersion ?? '-'}`,
      },
    ],
    [],
  );
  const summary = data.summary || {};
  return (
    <Stack
      dir="rtl"
      sx={{
        display: 'grid',
        gap: '16px',
        maxWidth: '1680px',
        width: '100%',
        margin: '0 auto',
        height: '100%',
        minHeight: 0,
        gridTemplateRows: 'auto minmax(0,1fr)',
        overflow: 'hidden',
      }}
    >
      <PageHeader title="דיווחים אוטומטיים" meta={`${data.total} אירועים`}>
        <FilterPanel>
          <TextField
            select
            value={filters.eventType}
            onChange={(e) =>
              setFilters({
                ...filters,
                eventType: e.target.value,
              })
            }
            fullWidth
          >
            <MenuItem value="">כל האירועים</MenuItem>
            {Object.entries(EVENT_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            value={filters.severity}
            onChange={(e) =>
              setFilters({
                ...filters,
                severity: e.target.value,
              })
            }
            fullWidth
          >
            <MenuItem value="">כל הרמות</MenuItem>
            {Object.entries(SEVERITY_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            placeholder="Place ID"
            value={filters.placeId}
            onChange={(e) =>
              setFilters({
                ...filters,
                placeId: e.target.value,
              })
            }
            fullWidth
          />

          <TextField
            placeholder="Device ID"
            value={filters.deviceId}
            onChange={(e) =>
              setFilters({
                ...filters,
                deviceId: e.target.value,
              })
            }
            fullWidth
          />

          <TextField
            type="date"
            value={filters.from}
            onChange={(e) =>
              setFilters({
                ...filters,
                from: e.target.value,
              })
            }
            fullWidth
          />

          <TextField
            type="date"
            value={filters.to}
            onChange={(e) =>
              setFilters({
                ...filters,
                to: e.target.value,
              })
            }
            fullWidth
          />
          <Button onClick={() => load(1)} disabled={loading}>
            סינון
          </Button>
        </FilterPanel>
      </PageHeader>
      <Stack
        sx={(theme) => ({
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr)',
          gap: '16px',
          minHeight: 0,
          overflow: 'hidden',
          [theme.breakpoints.down('sm')]: {
            display: 'flex',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            paddingBottom: 1,
          },
        })}
      >
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(3,minmax(140px,1fr))',
            gap: '12px',
            [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
          })}
        >
          <SummaryCard label="סה״כ אירועים" value={summary.total} />
          <SummaryCard label="תקלות ודחיות" value={summary.failures} tone="danger" />
          <SummaryCard label="מכשירים" value={summary.devices} tone="info" />
        </Stack>
        <Stack sx={{ minHeight: { xs: 520, sm: 0 } }}>
          {error && (
            <Typography color="error" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}
          <DataTable
            columns={columns}
            items={data.items}
            rowKey={(event) => event.eventId || event._id}
            onRowClick={setSelectedEvent}
            selectedRowKey={selectedEvent?.eventId || selectedEvent?._id}
            page={data.page}
            limit={data.limit}
            total={data.total}
            onPageChange={load}
            loading={loading}
            wide
          />{' '}
        </Stack>
      </Stack>
      <EventDialog event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </Stack>
  );
}
